import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Minus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useGetCompanies } from '@/services/api/endpoints/CompanyService';
import {
  useGetUserWallets,
  useAdjustWalletBalance,
} from '@/services/api/endpoints/WalletService';
import { formatAmount } from '@/lib/utils';
import type { User } from '@/types';

interface TopUpWalletModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
}

export function TopUpWalletModal({ open, onOpenChange, user }: TopUpWalletModalProps) {
  const { t } = useTranslation();

  const [companyId, setCompanyId] = useState<string>('');
  const [type, setType] = useState<'credit' | 'debit'>('credit');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const { data: companiesData, isLoading: companiesLoading } = useGetCompanies(1, 100);
  const { data: userWallets, isLoading: walletsLoading } = useGetUserWallets(
    open ? user?.id : undefined
  );
  const adjustMutation = useAdjustWalletBalance();

  // Map of companyId → balance for quick lookup
  const walletByCompany = useMemo(() => {
    const m = new Map<string, number>();
    userWallets?.forEach((w) => m.set(w.companyId, Number(w.balance)));
    return m;
  }, [userWallets]);

  // Sort: companies where user has a wallet first, then the rest
  const sortedCompanies = useMemo(() => {
    const list = companiesData?.items || [];
    return [...list].sort((a, b) => {
      const aHas = walletByCompany.has(a.id) ? 0 : 1;
      const bHas = walletByCompany.has(b.id) ? 0 : 1;
      if (aHas !== bHas) return aHas - bHas;
      return a.name.localeCompare(b.name);
    });
  }, [companiesData?.items, walletByCompany]);

  // Reset state when opening; preselect the user's primary company
  useEffect(() => {
    if (open) {
      setAmount('');
      setReason('');
      setType('credit');
      setCompanyId(user?.company?.id || '');
    }
  }, [open, user?.company?.id]);

  const selectedBalance = companyId ? walletByCompany.get(companyId) : undefined;
  const walletExists = selectedBalance !== undefined;

  const handleSubmit = async () => {
    if (!user || !companyId) {
      toast.error(t('users.topUp.selectCompany'));
      return;
    }
    const amountRsd = Math.round(parseFloat(amount));
    if (isNaN(amountRsd) || amountRsd <= 0) {
      toast.error(t('wallets.invalidAmount'));
      return;
    }
    if (!reason.trim()) {
      toast.error(t('users.topUp.reasonRequired'));
      return;
    }

    try {
      await adjustMutation.mutateAsync({
        userId: user.id,
        companyId,
        amount: amountRsd,
        type,
        reason: reason.trim(),
      });
      toast.success(
        walletExists
          ? t('wallets.adjustSuccess')
          : t('users.topUp.walletCreatedAndCredited')
      );
      onOpenChange(false);
    } catch {
      toast.error(t('wallets.adjustError'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{t('users.topUp.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="text-sm text-muted-foreground">
            {t('wallets.adjustingFor')}{' '}
            <span className="font-medium text-foreground">{user?.email}</span>
          </div>

          {/* Type toggle */}
          <div className="flex rounded-md border overflow-hidden text-sm">
            <button
              type="button"
              onClick={() => setType('credit')}
              className={`flex-1 px-3 py-2 font-medium transition-colors flex items-center justify-center gap-1.5 ${
                type === 'credit'
                  ? 'bg-green-600 text-white'
                  : 'bg-background hover:bg-muted'
              }`}
            >
              <Plus className="h-4 w-4" />
              {t('wallets.credit')}
            </button>
            <button
              type="button"
              onClick={() => setType('debit')}
              className={`flex-1 px-3 py-2 font-medium transition-colors flex items-center justify-center gap-1.5 ${
                type === 'debit'
                  ? 'bg-red-600 text-white'
                  : 'bg-background hover:bg-muted'
              }`}
            >
              <Minus className="h-4 w-4" />
              {t('wallets.debit')}
            </button>
          </div>

          {/* Company / wallet picker */}
          <div className="space-y-2">
            <Label>{t('users.topUp.wallet')}</Label>
            {companiesLoading || walletsLoading ? (
              <Skeleton className="h-10" />
            ) : (
              <Select value={companyId} onValueChange={setCompanyId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('users.topUp.selectCompany')} />
                </SelectTrigger>
                <SelectContent>
                  {sortedCompanies.map((c) => {
                    const bal = walletByCompany.get(c.id);
                    return (
                      <SelectItem key={c.id} value={c.id}>
                        <span className="flex items-center gap-2">
                          <span>{c.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {bal !== undefined
                              ? `· ${formatAmount(bal)}`
                              : `· ${t('users.topUp.noWalletYet')}`}
                          </span>
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
            {companyId && !walletExists && (
              <p className="text-xs text-muted-foreground">
                {t('users.topUp.willBeCreated')}
              </p>
            )}
            {companyId && walletExists && (
              <p className="text-xs text-muted-foreground">
                {t('wallets.currentBalance')}:{' '}
                <span className="font-medium text-foreground">
                  {formatAmount(selectedBalance!)}
                </span>
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>{t('wallets.amount')} (RSD)</Label>
            <Input
              type="number"
              min="1"
              step="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
            />
          </div>

          <div className="space-y-2">
            <Label>{t('wallets.reason')}</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('wallets.reasonPlaceholder')}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={adjustMutation.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              adjustMutation.isPending ||
              !companyId ||
              !amount ||
              !reason.trim()
            }
            variant={type === 'credit' ? 'default' : 'destructive'}
          >
            {adjustMutation.isPending
              ? t('common.saving')
              : type === 'credit'
                ? t('wallets.credit')
                : t('wallets.debit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
